import { EditFreightDetailsDialog } from '@/components/Chat/EditFreightDetailsDialog'
import { EditTravelDistanceDialog } from '@/components/Chat/EditTravelDistanceDialog'
import { Button } from '@/components/ui/button'
import type { InsuranceApplication } from '@/models/insuranceApplication'
import type { TravelDistanceItem } from '@/models/user'
import { useSummaryEditor } from './summaryPopover'
import { SummaryList } from './SummaryPrimitives'

interface AdditionalInfoSummaryProps {
  application: InsuranceApplication
  /**
   * Distance and its options live on the org's currentInsuranceApplication summary, not the
   * application payload. An empty options list disables the distance editor.
   */
  travelDistance: TravelDistanceItem[] | null
  travelDistanceOptions: TravelDistanceItem[]
  onSuccess: () => void
  /** Shows the freight mix and driving distance with no way to change them. */
  readonly?: boolean
}

/** Freight mix and driving distance — the two things underwriters most often re-price on. */
export function AdditionalInfoSummary({
  application,
  travelDistance,
  travelDistanceOptions,
  onSuccess,
  readonly,
}: AdditionalInfoSummaryProps) {
  const freightEditor = useSummaryEditor()
  const distanceEditor = useSummaryEditor()

  const commodities = application.commodities ?? []
  const distances = travelDistance ?? []

  return (
    <div className="flex flex-col gap-6">
      {!readonly && (
        <>
          <EditFreightDetailsDialog
            open={freightEditor.isOpen}
            onOpenChange={freightEditor.setOpen}
            onSuccess={onSuccess}
            commodities={commodities}
            orgId={application.orgId}
          />
          <EditTravelDistanceDialog
            open={distanceEditor.isOpen}
            onOpenChange={distanceEditor.setOpen}
            onSuccess={onSuccess}
            options={travelDistanceOptions}
            selected={travelDistance}
            orgId={application.orgId}
          />
        </>
      )}

      <section className="flex flex-col gap-2">
        <h3 className="text-ink-500 text-xs font-semibold tracking-wide uppercase">Freight details</h3>
        <SummaryList
          items={commodities.map((item) => `${item.name} (${item.percentage}%)`)}
          empty="No commodities on file."
        />
        {!readonly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={freightEditor.open}
          >
            Edit freight details
          </Button>
        )}
      </section>

      {/* Divider between sections, matching Business details. */}
      <section className="border-ink-100 flex flex-col gap-2 border-t pt-4">
        <h3 className="text-ink-500 text-xs font-semibold tracking-wide uppercase">Driving distance</h3>
        <SummaryList
          items={distances.map((distance: TravelDistanceItem) => distance.label)}
          empty="No driving distance on file."
        />
        {!readonly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            disabled={travelDistanceOptions.length === 0}
            onClick={distanceEditor.open}
          >
            Edit driving distance
          </Button>
        )}
      </section>
    </div>
  )
}
